<script setup lang="ts">
import {
  BaseButtonKindEnum,
  BaseButtonWeightEnum,
} from "@/components/Base/Button";
import { type Props as BaseButtonProps } from "@/components/Base/Button.vue";
import { type Props as BaseIconProps } from "@/components/Base/Icon/index.vue";

const { getContent } = useSlotsWithSlotContent();

export type Props = BaseButtonProps & {
  label?: string;
  value?: number | string;
  showValue?: boolean;
  iconName?: BaseIconProps["iconName"];
  iconSvg?: BaseIconProps["iconSvg"];
  iconPosition?: "left" | "right";
  noWrap?: boolean;
  iconOnly?: boolean;
  iconColor?: BaseIconProps["color"];
  iconBgColor?: BaseIconProps["bgColor"];
  labelStyle?: string;
  buttonClass?: string;
  kind?: `${BaseButtonKindEnum}`;
  weight?: `${BaseButtonWeightEnum}`;
};

const props = withDefaults(defineProps<Props>(), {
  label: "",
  value: undefined,
  iconName: undefined,
  iconSvg: undefined,
  iconPosition: "right",
  kind: "link",
  iconColor: "inherit",
  iconBgColor: undefined,
  to: undefined,
  labelStyle: "",
  buttonClass: undefined,
  weight: "normal",
});

const emit = defineEmits(["icon-click"]);

const hasSlotContent = computed(() => getContent("default"));
</script>

<template>
  <BaseButton
    v-bind="{ ...props, ...$attrs }"
    class="base-icon-button"
    :class="{
      'flex-row-reverse': iconPosition === 'left',
      'text-nowrap': noWrap,
      'has-slot-content': hasSlotContent,

      [buttonClass]: buttonClass,
    }"
    :aria-label="ariaLabel || label || value"
  >
    <template v-if="hasSlotContent">
      <span v-if="!iconOnly">
        <slot></slot>
      </span>
    </template>

    <template v-else>
      <span v-if="label && !iconOnly" :style="labelStyle">
        {{ label }}
      </span>
      <span v-if="showValue" class="me-1">{{ value }}</span>
    </template>

    <BaseIcon
      v-if="iconName || iconSvg"
      :icon-name="iconName"
      :icon-svg="iconSvg"
      :color="iconColor"
      :bg-color="iconBgColor"
      :size="size"
      :circle="iconOnly"
    />
  </BaseButton>
</template>

<style scoped lang="scss">
.base-icon-button {
  display: inline-flex;
  text-decoration: none;
  width: fit-content;
  align-items: center;
  gap: 0.5rem;

  :deep(.btn-flex) {
    display: inline-flex;
    align-items: center;
    gap: inherit;
  }

  &--circle {
    padding: 0.25rem;
    border-radius: 50px;

    i {
      width: 1rem;
      height: 1rem;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
}
</style>
